import SegmentsApi from "@/api/segments";
import { useToastNotificationStore } from "@/experiences/toast-notifications/store/toast-notifications";
import { defineStore } from "pinia";
import { computed, inject, ref } from "vue";
import { SegmentStatus, SegmentType } from "@/types/segments";
// Global
const SEGMENTS_PATH = "/segments";
// Store definition
export const useSegmentsStore = defineStore("segments", () => {
    const i18n = inject("i18n");
    const notifStore = useToastNotificationStore();
    const backendErrorHandler = (err) => {
        const { type, error } = err;
        if (type === "SiteLimitException") {
            const resourceError = error?.toString();
            notifStore.addFatalSaveErrorNotice(err, i18n.t("Limit set for active {0} cannot be exceeded", [
                resourceError === "SMART_SEGMENTS" ? i18n.t("Smart Segments") : i18n.t("Basic Segments")
            ]));
            return;
        }
        notifStore.addFatalSaveErrorNotice(err);
    };
    const segments = ref([]);
    const segmentsMap = computed(() => new Map(segments.value.map((s) => [s.segmentId, s])));
    const basicSegments = computed(() => segments.value.filter((s) => s.type === SegmentType.Basic));
    const smartSegments = computed(() => segments.value.filter((s) => s.type === SegmentType.Smart));
    const pending = ref(false);
    const getSegmentIndex = (segment) => segments.value.findIndex((s) => s.segmentId === segment.segmentId);
    const storeSegment = (segment) => {
        const currentIndex = getSegmentIndex(segment);
        if (currentIndex < 0) {
            segments.value.push(segment);
        }
        else {
            segments.value.splice(currentIndex, 1, segment);
        }
    };
    const clearSegments = () => {
        segments.value = [];
    };
    const overwriteStoredSegments = (newSegments) => {
        clearSegments();
        segments.value.push(...newSegments);
    };
    const getSegments = async () => {
        pending.value = true;
        try {
            const res = await SegmentsApi.getSegments();
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            // Prevents iterating on response items
            overwriteStoredSegments(data);
            pending.value = false;
            return data;
        }
        catch (error) {
            backendErrorHandler(error);
            pending.value = false;
        }
    };
    const createSegment = async (segment, studyId) => {
        pending.value = true;
        try {
            const res = await SegmentsApi.createSegment(segment, studyId);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            storeSegment(data);
            notifStore.addSuccessNotice(i18n.t("Segment {1}{0}{2} created", [segment.name, "<strong>", "</strong>"]));
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
        }
        finally {
            pending.value = false;
        }
    };
    const updateSegment = async (segment) => {
        pending.value = true;
        try {
            const res = await SegmentsApi.updateSegment(segment);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            storeSegment(data);
            pending.value = false;
            notifStore.addSuccessNotice(i18n.t("Segment {0} updated", [segment.name]));
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
            pending.value = false;
        }
    };
    const bulkSegmentUpdate = async (segments) => {
        if (segments.length === 0)
            return [];
        pending.value = true;
        try {
            const res = await SegmentsApi.bulkSegmentUpdate(segments);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            data.map(storeSegment);
            pending.value = false;
            notifStore.addSuccessNotice(segments.length === 1
                ? i18n.t("Segment {0} updated", [`<strong>${segmentsMap.value.get(segments[0].segmentId)?.name}</strong>`])
                : i18n.t("{0} segment(s) updated", [`<strong>${segments.length}</strong>`]));
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
            pending.value = false;
        }
    };
    const deleteSegment = async (segment) => {
        pending.value = true;
        try {
            const res = await SegmentsApi.deleteSegment(segment);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            // Since no data is returned in server's response, stored deleted segments are updated locally.
            storeSegment({
                ...segment,
                status: SegmentStatus.Deleted,
                deletedOn: new Date().toISOString()
            });
            pending.value = false;
            notifStore.addSuccessNotice(i18n.t("Segment {0} deleted", [`<strong>${segment.name}</strong>`]), {
                svgIcon: "icon-trash"
            });
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
            pending.value = false;
        }
    };
    const bulkSegmentDelete = async (segments) => {
        if (segments.length === 0)
            return [];
        pending.value = true;
        try {
            const res = await SegmentsApi.bulkSegmentDelete(segments);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            // Since no data is returned in server's response, stored deleted segments are updated locally.
            segments
                .map((s) => ({
                ...s,
                status: SegmentStatus.Deleted,
                deletedOn: new Date().toISOString()
            }))
                .map(storeSegment);
            notifStore.addSuccessNotice(i18n.t(`{0} segment(s) deleted`, [segments.length]), { svgIcon: "icon-trash" });
            pending.value = false;
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
            pending.value = false;
        }
    };
    const restoreSegment = async (segment) => {
        pending.value = true;
        try {
            const res = await SegmentsApi.restoreSegment(segment);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            storeSegment(data);
            pending.value = false;
            notifStore.addSuccessNotice(i18n.t("Segment {0} restored", [segment.name]));
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
            pending.value = false;
        }
    };
    const bulkSegmentRestore = async (segments) => {
        if (segments.length === 0)
            return [];
        pending.value = true;
        try {
            const res = await SegmentsApi.bulkSegmentRestore(segments);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            data.map(storeSegment);
            pending.value = false;
            notifStore.addSuccessNotice(i18n.t("{0} segment(s) restored", [segments.length]));
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
            pending.value = false;
        }
    };
    const archiveSegment = async (segment) => {
        pending.value = true;
        try {
            const res = await SegmentsApi.archiveSegment(segment);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            storeSegment(data);
            pending.value = false;
            notifStore.addSuccessNotice(i18n.t("Segment {0} archived", [segment.name]));
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
            pending.value = false;
        }
    };
    const bulkSegmentArchive = async (segments) => {
        if (segments.length === 0)
            return [];
        pending.value = true;
        try {
            const res = await SegmentsApi.bulkSegmentArchive(segments);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            data.map(storeSegment);
            pending.value = false;
            notifStore.addSuccessNotice(i18n.t("{0} segment(s) archived", [segments.length]));
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
            pending.value = false;
        }
    };
    const unarchiveSegment = async (segment) => {
        pending.value = true;
        try {
            const res = await SegmentsApi.unarchiveSegment(segment);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            storeSegment(data);
            pending.value = false;
            notifStore.addSuccessNotice(i18n.t("Segment {0} unarchived", [segment.name]));
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
            pending.value = false;
        }
    };
    const bulkSegmentUnarchive = async (segments) => {
        if (segments.length === 0)
            return [];
        pending.value = true;
        try {
            const res = await SegmentsApi.bulkSegmentUnarchive(segments);
            if (res.statusCode)
                return backendErrorHandler(res);
            const data = res;
            data.map(storeSegment);
            pending.value = false;
            notifStore.addSuccessNotice(i18n.t("{0} segment(s) unarchived", [segments.length]));
            return data;
        }
        catch (err) {
            backendErrorHandler(err);
            pending.value = false;
        }
    };
    // TODO: Verify if this is still necessary - this was just to make sure that segments loaded
    // from the page are formatted correctly.
    const segmentsStandardizer = (segments) => segments.map((segment) => ({
        ...segment,
        segmentId: segment.id ?? segment.segmentId,
        type: segment.type ?? SegmentType.Basic,
        status: segment.status ?? SegmentStatus.Active
    }));
    const onLoad = (incomingSegments) => {
        const { smart, basic } = incomingSegments;
        segments.value = segmentsStandardizer([
            ...(smart ?? []).map((s) => ({ ...s, type: SegmentType.Smart })),
            ...(basic ?? [])
        ]);
        return incomingSegments;
    };
    const load = () => {
        console.error("Segment data could not be loaded from page.");
        return Promise.resolve({});
    };
    return {
        pending,
        segments,
        segmentsMap,
        basicSegments,
        smartSegments,
        getSegments,
        createSegment,
        updateSegment,
        bulkSegmentUpdate,
        deleteSegment,
        bulkSegmentDelete,
        restoreSegment,
        bulkSegmentRestore,
        archiveSegment,
        bulkSegmentArchive,
        unarchiveSegment,
        bulkSegmentUnarchive,
        onLoad,
        load,
        storeSegment
    };
}, {
    loadable: {
        action: "load",
        reloadable: true,
        autoload: true,
        fromPage: {
            key: SEGMENTS_PATH,
            onLoad: "onLoad"
        }
    }
});
